import { useMemo } from 'react'

import { TagGroup } from '../../../../api/core'
import { useLiveEventTagGroups } from '../../../../features/live-events/hooks/useLiveEventTagGroups'
import { useGetTrackedBlueprintsQuery } from '../../../api/markets'

export type BlueprintFamilyEventTypeInfo = {
  eventTypeId?: string
  eventCategoryId?: string
}

type BlueprintFamilyEventTypesResult = {
  eventTypeInfoByBlueprintId: { [blueprintFamilyId: string]: BlueprintFamilyEventTypeInfo }
  eventCategories: TagGroup[]
  isLoading: boolean
}

/**
 * Event-type categorization for Blueprint Families, for grouping and filtering in the
 * internal tooling.
 *
 * Sourced from the INTERNAL list endpoint, which carries the same `eventTypes` ordering
 * as its customer-facing counterpart but still returns discontinued families. The
 * customer endpoint dropped them entirely in Live Event Analytics 1.0, which would leave
 * every discontinued family uncategorized here whenever "Show discontinued" is on — and
 * that toggle defaults to on. The internal list is also what `useBlueprintFamilyUsage`
 * already fetches, so this shares its cache entry rather than adding a request.
 *
 * This is a single-type grouping for a one-column internal listing, so it takes the
 * family's leading event type — `eventTypes` is ordered by count descending. A family
 * spanning several types is filed under that one here, unlike the customer catalogue
 * which lists it under each.
 *
 * Categorization is best-effort: if the request fails the map stays empty and blueprints
 * fall back to uncategorized instead of blocking the page. A family with no active event
 * carries no event types and is left uncategorized too.
 */
export const useBlueprintFamilyEventTypes = (): BlueprintFamilyEventTypesResult => {
  const blueprintFamiliesQuery = useGetTrackedBlueprintsQuery()
  const tagGroupsQuery = useLiveEventTagGroups()

  const eventTypeInfoByBlueprintId = useMemo(() => {
    return (blueprintFamiliesQuery.data || []).reduce<{ [blueprintFamilyId: string]: BlueprintFamilyEventTypeInfo }>((acc, blueprint) => {
      const leading = blueprint.eventTypes?.[0]
      if (leading) {
        acc[blueprint.id] = { eventTypeId: leading.typeId, eventCategoryId: leading.categoryId }
      }
      return acc
    }, {})
  }, [blueprintFamiliesQuery.data])

  return {
    eventTypeInfoByBlueprintId,
    // Already sorted by `order` and translated by the query's transformResponse.
    eventCategories: tagGroupsQuery.data || [],
    isLoading: blueprintFamiliesQuery.isLoading || tagGroupsQuery.isLoading,
  }
}
